import styles from './ReconTable.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { COUNT_COLUMN_KEYS, NO_DATA_COPY_BY_COLUMN_KEY, resolveNaCopy } from '../constants/reconciliationsCopy';
import { formatAmountInUnit } from '../UnitsContext';
import type { ReconCell, ReconColumn, ReconStatus } from '../../../../../store/reconciliations/types';

interface ReconCellContentProps {
    cell: ReconCell | undefined;
    column: ReconColumn;
    unit: string;
    /** Row-level caveat, appended to the cell's own reason inside the single tooltip. */
    caveat?: string;
}

const NA_TEXT = 'N/A';

const BAND_CLASS: Record<ReconStatus, string | undefined> = {
    RECONCILED: styles.CellReconciled,
    PARTIAL: styles.CellPartial,
    NOT_RECONCILED: styles.CellNotReconciled,
    NA: styles.CellNotAvailable,
    ANCHOR: undefined,
};

/** The ground behind a cell. An anchor column is the figure everything else is scored against, so
 *  it carries the reference wash rather than a tolerance band — keyed off the column, because the
 *  cell's own status is not a reliable anchor marker: `anchorCell(null)` degrades to `NA` and
 *  `contextCell` emits `ANCHOR` outright (underwriting-engine `builders/common.ts`). Context and
 *  derived columns stay on the plain table ground. */
export function cellBandClass(cell: ReconCell | undefined, column: ReconColumn): string | undefined {
    if (column.role === 'anchor') return styles.CellAnchor;
    if (!cell || column.role !== 'compared') return undefined;
    return BAND_CLASS[cell.status];
}

interface CellView {
    body: React.ReactNode;
    visibleText?: string;
    reason?: string;
}

/** The value/badge/N-A rendering an FY row and its drill-down children share, so a child row can
 *  never format a figure or explain an empty cell differently from the row it sits under. A cell's
 *  unscored reason and the caller's row caveat share ONE tooltip, so the two never nest. */
function ReconCellContent({ cell, column, unit, caveat }: Readonly<ReconCellContentProps>) {
    const { body, visibleText, reason } = cellView(cell, column, unit);
    const tooltipLines = [reason, caveat].filter(Boolean);
    const inner = (
        <span className={styles.CellInner} {...(reason && { role: 'group', 'aria-label': `${visibleText}. ${reason}` })}>
            {body}
        </span>
    );
    if (!tooltipLines.length) return inner;
    const content = tooltipLines.map((line) => <div key={line}>{line}</div>);
    return (
        <Tooltip content={content} direction="top" usePortal fitContent containerFullWidth>
            {inner}
        </Tooltip>
    );
}

// An unscored cell shows only its figure (muted, no badge) or N/A; the reason lives in the tooltip
// by product decision. The one visible phrase is a column-specific NO_DATA copy (EPFO "no debit found").
function cellView(cell: ReconCell | undefined, column: ReconColumn, unit: string): CellView {
    if (!cell || cell.value === null) {
        const columnPhrase = cell?.naReason === 'NO_DATA' ? NO_DATA_COPY_BY_COLUMN_KEY[column.key] : undefined;
        const visibleText = columnPhrase ?? NA_TEXT;
        const reason = columnPhrase ? undefined : resolveNaCopy(cell?.naReason, column.key, false);
        return { body: <span className={styles.CellNa}>{visibleText}</span>, visibleText, reason };
    }
    const amount = COUNT_COLUMN_KEYS.has(column.key)
        ? Math.round(cell.value).toLocaleString('en-IN')
        : formatAmountInUnit(cell.value, unit);
    if (cell.status === 'NA') {
        return {
            body: <span className={styles.CellNa}>{amount}</span>,
            visibleText: amount,
            reason: resolveNaCopy(cell.naReason, column.key, true),
        };
    }
    return {
        body: (
            <>
                {column.role === 'compared' && cell.score != null && <span className={styles.ScoreBadge}>{cell.score}%</span>}
                <span className={styles.CellValue}>{amount}</span>
            </>
        ),
    };
}

export default ReconCellContent;
